import { Web3DViewer } from '@technology/web3d';
import {
  type ICADObject,
  instanceOfICADObject,
  LineEditingModel,
} from '@technology/web3d-plugin-line-editing';
import { ILogger } from '@trimble/logging';
import {
  CAD_FEATURE_ID_KEY,
  CAD_SOURCE_FEATURE_KEY,
  CadGeometryReader,
  GeospatialFeature,
  geometryForLayerTemplate,
} from '@trimble/geospatial';
import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { LAYER_ID_METADATA_KEY } from './feature-extraction-cad-config';
import { batch } from 'solid-js';

export interface CreateCadSelectionSyncParams {
  readonly viewer: Web3DViewer;
  readonly model: LineEditingModel;
  readonly context: FeatureExtractionContextType;
  readonly geometryReader: CadGeometryReader;
  readonly logger: ILogger;
}

export interface CadSelectionSync {
  dispose(): void;
}

export function createCadSelectionSync(
  params: CreateCadSelectionSyncParams
): CadSelectionSync {
  const { viewer, model, context, geometryReader, logger } = params;
  let lastLocalSelectedFeatureId: string | undefined;

  const clearSharedSelectionIfOwnedLocally = (): void => {
    const previouslyLocal = lastLocalSelectedFeatureId;
    lastLocalSelectedFeatureId = undefined;
    const shared = context.editingState.selectedFeature;
    if (previouslyLocal !== undefined && shared?.id === previouslyLocal) {
      context.setSelectedFeature(undefined);
    }
  };

  const selectionListener = (): void => {
    // The viewer emits 'selection' before Models pushes the new ids into the
    // model, so model.getSelectedGuids() is still the previous selection here.
    // viewer.selection already holds the new ids.
    const ids = viewer.selection.get(model.modelId) ?? [];
    if (ids.length === 0) {
      clearSharedSelectionIfOwnedLocally();
      return;
    }
    const entities: unknown[] = model.getEntitiesByIds(...ids);
    const first: ICADObject | undefined = entities.find(instanceOfICADObject);
    if (!first) {
      clearSharedSelectionIfOwnedLocally();
      return;
    }
    const metadata = first.metaData as Record<string, unknown>;
    const layerId = readString(metadata[LAYER_ID_METADATA_KEY]);
    const featureId = readString(metadata[CAD_FEATURE_ID_KEY]) ?? first.guid;
    const sourceFeature = metadata[CAD_SOURCE_FEATURE_KEY] as
      | GeospatialFeature
      | undefined;
    const sharedFeature =
      featureId !== undefined ? context.getFeature(featureId) : undefined;
    const { [CAD_SOURCE_FEATURE_KEY]: _sourceFeature, ...exposedMetadata } =
      metadata;
    const selectedLayerId =
      layerId ??
      sharedFeature?.layerId ??
      sourceFeature?.layerId ??
      context.editingState.selectedLayerId ??
      '';
    const selected: GeospatialFeature = {
      id: featureId,
      layerId: selectedLayerId,
      geometry: geometryForLayerTemplate(
        geometryReader.toGeometry(first),
        context.editingState.layers.find(
          (layer) => layer.id === selectedLayerId
        )?.template?.geometryType,
        sourceFeature?.geometry.type ?? sharedFeature?.geometry.type
      ),
      properties: {
        ...(sourceFeature?.properties ?? {}),
        ...(sharedFeature?.properties ?? {}),
      },
      metadata: {
        ...(sourceFeature?.metadata ?? {}),
        ...exposedMetadata,
      },
    };
    lastLocalSelectedFeatureId = featureId;
    batch(() => {
      if (layerId) {
        context.setSelectedLayerId(layerId);
      }
      context.setSelectedFeature(selected);
    });
    logger.debug('Selection changed', { featureId, layerId });
  };
  viewer.addEventListener('selection', selectionListener);

  return {
    dispose: () => {
      viewer.removeEventListener('selection', selectionListener);
    },
  };
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
